import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import RichTextEditor from '../components/RichTextEditor';
import ContactEmailTypeahead from '../components/ContactEmailTypeahead';
import { useContacts } from '../hooks/useContacts';
import { useAccounts } from '../hooks/useAccounts';
import { useQueryClient } from '@tanstack/react-query';
import {
    useInboxEmails, useCommunications, useGmailStatus, useGmailEmails,
    useEmailTemplates, useSignatures, useCalendarStatus, useCalendarEvents,
    useSendEmail, useSyncGmail, useLogCommunication, useArchiveEmail, useAddContactFromEmail,
    useTrackedEmails,
} from '../hooks/useInbox';
import { useTrackingSSE } from '../hooks/useTrackingSSE';
import TrackingBadge, { TrackingStatusBadge } from '../components/TrackingBadge';
import {
    Search, Mail, Phone, MessageSquare, CheckSquare, Bell, Users,
    Filter, Calendar, ChevronLeft, ChevronRight, X, Plus, Trash2,
    Star, Archive, Reply, MoreHorizontal, Clock, Eye, MousePointer,
    Send, UserPlus, Settings, HelpCircle, ExternalLink, FileSignature,
    Layers, Variable, Sparkles, RefreshCw, Loader2, Inbox as InboxIcon,
    Maximize2, Minimize2, Mic, MicOff, MessageCircle
} from 'lucide-react';
import WhatsAppChat from '../components/WhatsAppChat';
import { useTasks, useCreateTask, useCompleteTask, useReopenTask } from '../hooks/useTasks';
import { signatureAssetMergeMap } from '../lib/signatureAssets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Voice-to-Text Button ────────────────────────────────────────────────── */
function VoiceButton({ onResult, testId, isTextarea }) {
    const [listening, setListening] = useState(false);

    const toggle = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) { toast.error('Voice input not supported in this browser'); return; }

        if (listening) { setListening(false); return; }

        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = 'en-US';

        recognition.onresult = (event) => {
            const text = event.results[0][0].transcript;
            onResult(text);
            setListening(false);
        };
        recognition.onerror = () => { setListening(false); toast.error('Voice recognition failed'); };
        recognition.onend = () => setListening(false);

        recognition.start();
        setListening(true);
    };

    return (
        <button
            type="button"
            onClick={toggle}
            data-testid={testId}
            title={listening ? 'Stop listening' : 'Voice input'}
            className={`absolute right-2 ${isTextarea ? 'top-2' : 'top-1/2 -translate-y-1/2'} p-1.5 rounded-lg transition-colors ${
                listening
                    ? 'bg-red-100 text-red-600 animate-pulse'
                    : 'text-stone-400 hover:text-stone-700 hover:bg-stone-100'
            }`}
        >
            {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>
    );
}

// Tab configuration
const INBOX_TABS = [
    { key: 'primary', label: 'Primary', icon: Mail },
    { key: 'emails', label: 'Emails', icon: Mail },
    { key: 'tracking', label: 'Tracking', icon: Eye },
    { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
    { key: 'calls', label: 'Calls', icon: Phone },
    { key: 'messages', label: 'Messages', icon: MessageSquare },
    { key: 'tasks', label: 'Tasks', icon: CheckSquare },
    { key: 'reminders', label: 'Reminders', icon: Bell },
];

/* ── Schedule Meeting Modal ─────────────────────────────────────────────── */
function ScheduleMeetingModal({ open, onClose, getAuthHeader, onCreated }) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [date, setDate] = useState('');
    const [startTime, setStartTime] = useState('09:00');
    const [endTime, setEndTime] = useState('09:30');
    const [attendees, setAttendees] = useState('');
    const [addMeet, setAddMeet] = useState(true);
    const [loading, setLoading] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [selectedContactIds, setSelectedContactIds] = useState([]);

    useEffect(() => {
        if (open) {
            const today = new Date().toISOString().split('T')[0];
            setDate(today);
            setTitle('');
            setDescription('');
            setStartTime('09:00');
            setEndTime('09:30');
            setAttendees('');
            setAddMeet(true);
            setSelectedContactIds([]);
            // Load contacts
            axios.get(`${API}/contacts`, { headers: getAuthHeader() })
                .then(res => setContacts(Array.isArray(res.data) ? res.data : res.data.contacts || []))
                .catch(() => {});
        }
    }, [open, getAuthHeader]);

    const handleSubmit = async () => {
        if (!title || !date || !startTime || !endTime) return toast.error('Title, date and times are required');
        setLoading(true);
        try {
            const start = `${date}T${startTime}:00`;
            const end = `${date}T${endTime}:00`;
            const attendeeList = attendees.split(/[,;\n]/).map(e => e.trim()).filter(Boolean);
            // Add selected contact emails
            selectedContactIds.forEach(cid => {
                const c = contacts.find(x => x.contact_id === cid);
                if (c?.email && !attendeeList.includes(c.email)) attendeeList.push(c.email);
            });
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
            const res = await axios.post(`${API}/oauth/calendar/events`, {
                title, description, start, end,
                attendees: attendeeList,
                add_google_meet: addMeet,
                timezone: tz,
                contact_ids: selectedContactIds,
            }, { headers: getAuthHeader() });
            toast.success('Meeting scheduled!');
            if (onCreated) onCreated(res.data);
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to schedule meeting');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-lg" data-testid="schedule-meeting-modal">
                <DialogHeader>
                    <DialogTitle>Schedule Meeting</DialogTitle>
                    <DialogDescription>Create a new meeting on your Google Calendar</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <div>
                        <Label className="text-xs font-semibold text-stone-500 uppercase">Title</Label>
                        <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Meeting title" className="mt-1" data-testid="meeting-title" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label className="text-xs font-semibold text-stone-500 uppercase">Date</Label>
                            <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="mt-1" data-testid="meeting-date" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-stone-500 uppercase">Start</Label>
                            <Input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="mt-1" data-testid="meeting-start" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-stone-500 uppercase">End</Label>
                            <Input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="mt-1" data-testid="meeting-end" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold text-stone-500 uppercase">Description</Label>
                        <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Meeting agenda or notes..." rows={2} className="mt-1" data-testid="meeting-description" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold text-stone-500 uppercase">Invite Attendees (emails)</Label>
                        <Input value={attendees} onChange={e => setAttendees(e.target.value)} placeholder="email@company.com, another@co.com" className="mt-1" data-testid="meeting-attendees" />
                    </div>
                    {contacts.length > 0 && (
                        <div>
                            <Label className="text-xs font-semibold text-stone-500 uppercase">Link CRM Contacts</Label>
                            <div className="mt-1 max-h-28 overflow-y-auto border border-stone-200 rounded-lg p-2 space-y-1">
                                {contacts.slice(0, 20).map(c => (
                                    <label key={c.contact_id} className="flex items-center gap-2 text-sm hover:bg-stone-50 px-1 py-0.5 rounded cursor-pointer">
                                        <Checkbox
                                            checked={selectedContactIds.includes(c.contact_id)}
                                            onCheckedChange={(checked) => {
                                                if (checked) setSelectedContactIds(prev => [...prev, c.contact_id]);
                                                else setSelectedContactIds(prev => prev.filter(id => id !== c.contact_id));
                                            }}
                                        />
                                        <span className="truncate">{c.first_name} {c.last_name}</span>
                                        {c.email && <span className="text-xs text-stone-400 ml-auto truncate">{c.email}</span>}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                    <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                        <Checkbox checked={addMeet} onCheckedChange={setAddMeet} data-testid="meeting-add-meet" />
                        Add Google Meet video call
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={loading} className="bg-stone-900 text-white hover:bg-stone-800" data-testid="meeting-submit">
                        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
                        Schedule
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ── Inbox Tasks Tab ────────────────────────────────────────────────────── */
function InboxTasksTab() {
    const navigate = useNavigate();
    const { data: tasks = [], isLoading } = useTasks({ status: 'planned', sort_by: 'due_date', sort_dir: 'asc' });
    const createTask = useCreateTask();
    const completeTask = useCompleteTask();
    const reopenTask = useReopenTask();
    const [quickTitle, setQuickTitle] = useState('');

    // Only show overdue + today tasks
    const focusedTasks = tasks.filter(t => t.date_group === 'overdue' || t.date_group === 'today');
    const overdueTasks = focusedTasks.filter(t => t.date_group === 'overdue');
    const todayTasks = focusedTasks.filter(t => t.date_group === 'today');

    const handleQuickAdd = (e) => {
        e.preventDefault();
        if (!quickTitle.trim()) return;
        const today = new Date().toISOString().split('T')[0];
        createTask.mutate({
            title: quickTitle.trim(),
            due_date: today,
            priority: 'medium',
            source: 'manual',
        }, {
            onSuccess: () => { setQuickTitle(''); toast.success('Task created'); },
            onError: () => toast.error('Failed to create task'),
        });
    };

    const handleToggle = (task) => {
        if (task.status === 'completed') reopenTask.mutate(task.activity_id);
        else completeTask.mutate(task.activity_id);
    };

    const TaskItem = ({ task }) => {
        const isCompleted = task.status === 'completed';
        const isOverdue = task.date_group === 'overdue';
        return (
            <div className="flex items-center px-4 py-2.5 hover:bg-stone-50 border-b border-stone-100 transition-colors">
                <button
                    onClick={() => handleToggle(task)}
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 shrink-0 transition-colors ${
                        isCompleted
                            ? 'bg-emerald-500 border-emerald-500 text-white'
                            : 'border-stone-300 hover:border-stone-500'
                    }`}
                >
                    {isCompleted && <Check className="w-3 h-3" />}
                </button>
                <div className="flex-1 min-w-0 mr-3">
                    <span className={`text-sm ${isCompleted ? 'line-through text-stone-400' : 'text-stone-900'}`}>
                        {task.title}
                    </span>
                    {task.contact_name && (
                        <button
                            onClick={() => navigate(`/contacts/${task.contact_id}`)}
                            className="flex items-center gap-1 text-[11px] text-stone-400 hover:text-blue-600 mt-0.5"
                        >
                            <Users className="w-3 h-3" /> {task.contact_name}
                        </button>
                    )}
                </div>
                {task.due_date && (
                    <span className={`text-xs shrink-0 ${isOverdue ? 'text-red-600 font-medium' : 'text-stone-500'}`}>
                        {isOverdue ? 'Overdue' : 'Today'}
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className="flex-1 overflow-y-auto">
            {/* Quick add */}
            <form onSubmit={handleQuickAdd} className="px-4 py-3 border-b border-stone-100">
                <div className="flex items-center gap-2">
                    <Plus className="w-4 h-4 text-stone-400 shrink-0" />
                    <input
                        value={quickTitle}
                        onChange={(e) => setQuickTitle(e.target.value)}
                        placeholder="Quick add task for today..."
                        className="flex-1 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none bg-transparent"
                    />
                    {quickTitle.trim() && (
                        <button type="submit" disabled={createTask.isPending}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-stone-900 rounded-lg hover:bg-stone-800 disabled:opacity-50">
                            {createTask.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add'}
                        </button>
                    )}
                </div>
            </form>

            {isLoading ? (
                <div className="flex items-center justify-center h-40">
                    <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
                </div>
            ) : focusedTasks.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-stone-400">
                    <CheckSquare className="w-10 h-10 mb-2 opacity-30" />
                    <p className="text-sm font-medium text-stone-500">All caught up!</p>
                    <p className="text-xs mt-1">No overdue or today tasks</p>
                </div>
            ) : (
                <>
                    {overdueTasks.length > 0 && (
                        <div>
                            <div className="px-4 py-2 bg-red-50 border-b border-red-200 flex items-center gap-2">
                                <Clock className="w-3.5 h-3.5 text-red-600" />
                                <span className="text-xs font-semibold text-red-600">Overdue ({overdueTasks.length})</span>
                            </div>
                            {overdueTasks.map(t => <TaskItem key={t.activity_id} task={t} />)}
                        </div>
                    )}
                    {todayTasks.length > 0 && (
                        <div>
                            <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 flex items-center gap-2">
                                <Calendar className="w-3.5 h-3.5 text-blue-600" />
                                <span className="text-xs font-semibold text-blue-600">Today ({todayTasks.length})</span>
                            </div>
                            {todayTasks.map(t => <TaskItem key={t.activity_id} task={t} />)}
                        </div>
                    )}
                </>
            )}

            {/* View All link */}
            <div className="px-4 py-3 border-t border-stone-100">
                <button
                    onClick={() => navigate('/tasks')}
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                >
                    View All Tasks <ChevronRight className="w-3 h-3" />
                </button>
            </div>
        </div>
    );
}

export default function Inbox() {
    const { user, getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    const urlParams = new URLSearchParams(window.location.search);
    const initialTab = urlParams.get('tab') || 'primary';

    // ─── React Query: cached data (survives navigation) ─────────────────
    const { data: contactsData, isLoading: loadingContacts, error: contactsError } = useContacts();
    const { data: accountsData } = useAccounts();
    const { data: inboxData, isLoading: loadingInbox, error: inboxError } = useInboxEmails();
    const { data: communications = [], error: communicationsError } = useCommunications();
    const { data: gmailStatus, error: gmailStatusError } = useGmailStatus();
    const { data: gmailEmailsData, isFetching: loadingGmail, error: gmailEmailsError } = useGmailEmails(gmailStatus?.connected);
    const { data: templatesData } = useEmailTemplates();
    const { data: signaturesData } = useSignatures();
    const { data: calendarStatusData } = useCalendarStatus();

    // Derived values from cached data
    const contacts = contactsData || [];
    const accounts = accountsData || [];
    const emails = inboxData?.emails || [];
    
    // Debug: Log errors to console
    useEffect(() => {
        if (inboxError) console.error('Inbox emails error:', inboxError);
        if (gmailEmailsError) console.error('Gmail emails error:', gmailEmailsError);
        if (communicationsError) console.error('Communications error:', communicationsError);
    }, [inboxError, gmailEmailsError, communicationsError]);

    // ─── Email Tracking ──────────────────────────────────────────────────
    const { data: trackingData } = useTrackedEmails();
    const trackedEmails = trackingData?.data || [];

    const tabCounts = { ...(inboxData?.counts || { primary: 0, emails: 0, calls: 0, messages: 0, tasks: 0, reminders: 0 }), tracking: trackedEmails.length };
    const gmailConnected = gmailStatus?.connected || false;
    const gmailNeedsReconnect = gmailStatus?.needs_reconnect || false;
    const gmailEmail = gmailStatus?.email || '';
    const gmailEmails = gmailEmailsData || [];
    const emailTemplates = templatesData?.templates || [];
    const adminEmailTemplates = templatesData?.adminTemplates || [];
    const signatures = signaturesData?.signatures || [];
    const adminSignatures = signaturesData?.adminSignatures || [];
    const defaultSignature = signaturesData?.defaultSignature || null;
    const calendarConnected = calendarStatusData?.connected || false;
    const potentialContacts = emails.filter(e => !e.contact_id);

    // Show UI as soon as we have any cached data or first query resolves
    const loading = (loadingInbox || loadingContacts) && emails.length === 0 && contacts.length === 0;
    useTrackingSSE();  // Real-time notifications

    // ─── Mutations ──────────────────────────────────────────────────────
    const sendEmailMutation = useSendEmail();
    const syncGmailMutation = useSyncGmail();
    const logCommMutation = useLogCommunication();
    const archiveEmailMutation = useArchiveEmail();
    const addContactMutation = useAddContactFromEmail();

    // ─── UI-only state (ephemeral, OK to lose on navigation) ────────────
    const [activeTab, setActiveTab] = useState(initialTab);
    const [search, setSearch] = useState('');
    const [showMeetings, setShowMeetings] = useState(true);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [meetingDate, setMeetingDate] = useState(new Date());
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [showComposeModal, setShowComposeModal] = useState(false);
    const [showLogModal, setShowLogModal] = useState(false);
    const [logModalExpanded, setLogModalExpanded] = useState(false);
    const [showEmailDetail, setShowEmailDetail] = useState(false);
    const [showCcBcc, setShowCcBcc] = useState(false);
    const [cc, setCc] = useState([]);
    const [bcc, setBcc] = useState([]);
    const [composeForm, setComposeForm] = useState({
        to: '', subject: '', body: '', type: 'email'
    });
    const [trackEmail, setTrackEmail] = useState(true);
    const [logForm, setLogForm] = useState({
        type: 'call', contact_id: '', account_id: '', subject: '', notes: '', duration: ''
    });
    const [showTemplateBrowser, setShowTemplateBrowser] = useState(false);
    const [templateFilter, setTemplateFilter] = useState('all');
    const [recommendations, setRecommendations] = useState(null);
    const [loadingRecommendations, setLoadingRecommendations] = useState(false);

    // Calendar events for selected date (React Query)
    const { data: meetings = [] } = useCalendarEvents(meetingDate, calendarConnected);

    // Resolve signature merge variables with current user data
    const resolveSigVars = (html) => {
        if (!html) return html;
        // v2.16.38: SenderPhoto + CompanyLogo come from the permanent
        // proxy endpoint — the old 1h presigned URLs expired for
        // recipients reading the email more than an hour later.
        const replacements = {
            '{{SenderName}}': user?.name || '',
            '{{SenderTitle}}': user?.title || '',
            '{{SenderCompany}}': user?.org_name || user?.company_name || '',
            '{{SenderEmail}}': user?.email || '',
            '{{SenderPhone}}': user?.phone || '',
            '{{SenderLinkedIn}}': user?.linkedin_url || '',
            '{{SenderAddress}}': user?.address || '',
            ...signatureAssetMergeMap(user),
        };
        Object.entries(replacements).forEach(([key, val]) => {
            html = html.split(key).join(val || '');
        });
        return html;
    };

    // Auto-load default signature into compose body on first load
    useEffect(() => {
        if (defaultSignature && !composeForm.body) {
            const sigHtml = resolveSigVars(defaultSignature.content);
            setComposeForm(prev => ({ ...prev, body: prev.body || `<p></p><p></p>${sigHtml}` }));
        }
    }, [defaultSignature]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Action handlers (using mutations) ──────────────────────────────

    const handleSyncGmail = async () => {
        try {
            const res = await syncGmailMutation.mutateAsync();
            toast.success(`Synced ${res.data.synced} new emails from Gmail`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Gmail sync failed');
        }
    };

    const handleSendEmail = async () => {
        if (!composeForm.to || !composeForm.subject) {
            toast.error('Please fill in recipient and subject');
            return;
        }
        try {
            await sendEmailMutation.mutateAsync({
                to: composeForm.to,
                subject: composeForm.subject,
                body: composeForm.body,
                cc, bcc, gmailConnected,
                track: trackEmail && gmailConnected,
            });
            toast.success(gmailConnected ? 'Email sent via Gmail' : 'Email logged (connect Gmail to send directly)');
            setShowComposeModal(false);
            setComposeForm({ to: '', subject: '', body: defaultSignature?.content || '', type: 'email' });
            setCc([]);
            setBcc([]);
            setShowCcBcc(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send email');
        }
    };

    // Selection handlers
    const toggleSelectAll = () => {
        const currentItems = getFilteredItems();
        if (selectedIds.length === currentItems.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(currentItems.map(item => item.id || item.email_id || item.communication_id));
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    // Get filtered items based on active tab
    const getFilteredItems = () => {
        let items = [];
        
        if (activeTab === 'primary' || activeTab === 'emails') {
            // Merge sequence emails with Gmail emails
            const allEmails = [...emails, ...gmailEmails];
            items = allEmails.filter(e => {
                if (search) {
                    const searchLower = search.toLowerCase();
                    return (
                        e.contact_name?.toLowerCase().includes(searchLower) ||
                        e.contact_email?.toLowerCase().includes(searchLower) ||
                        e.subject?.toLowerCase().includes(searchLower) ||
                        e.body?.toLowerCase().includes(searchLower)
                    );
                }
                return true;
            });
            // Sort by date descending
            items.sort((a, b) => {
                const dateA = new Date(a.sent_at || a.scheduled_date || a.created_at || 0);
                const dateB = new Date(b.sent_at || b.scheduled_date || b.created_at || 0);
                return dateB - dateA;
            });
        } else {
            items = communications.filter(c => {
                if (c.type !== activeTab.slice(0, -1)) return false; // Remove 's' from tab name
                if (search) {
                    const searchLower = search.toLowerCase();
                    return (
                        c.subject?.toLowerCase().includes(searchLower) ||
                        c.notes?.toLowerCase().includes(searchLower)
                    );
                }
                return true;
            });
        }
        
        return items;
    };

    // Email actions
    const handleAddContact = async (email) => {
        try {
            const res = await addContactMutation.mutateAsync(email);
            toast.success(`Contact "${res.data.first_name} ${res.data.last_name}" created`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create contact');
        }
    };

    const handleDiscard = async (emailId) => {
        try {
            await archiveEmailMutation.mutateAsync(emailId);
            toast.success('Email archived');
        } catch (err) {
            toast.error('Failed to archive email');
        }
    };

    const handleReply = (email) => {
        const defaultSig = defaultSignature;
        setComposeForm({
            to: email.contact_email,
            subject: `Re: ${email.subject}`,
            body: defaultSig ? `<p><br></p>${defaultSig.content}` : '',
            type: 'email',
            reply_to_id: email.email_id
        });
        setShowComposeModal(true);
    };
    
    // Template and signature handlers
    const applyTemplate = (template) => {
        const defaultSig = defaultSignature;
        setComposeForm(prev => ({
            ...prev,
            subject: template.subject,
            body: defaultSig ? `${template.body}<p><br></p><p><br></p>${resolveSigVars(defaultSig.content)}` : template.body
        }));
        setShowTemplateBrowser(false);
        toast.success(`Template "${template.name}" applied`);
    };
    
    const insertSignature = (signature) => {
        const html = resolveSigVars(signature.content || '');
        const separator = composeForm.body ? '<p><br></p><p><br></p>' : '';
        setComposeForm(prev => ({
            ...prev,
            body: prev.body + separator + html
        }));
        toast.success(`Signature "${signature.name}" inserted`);
    };
    
    // Get all templates combined
    const allTemplates = [...emailTemplates, ...adminEmailTemplates];
    const filteredTemplates = templateFilter === 'all'
        ? allTemplates
        : allTemplates.filter(t => t.category === templateFilter);
    
    const getCategoryLabel = (value) => {
        const categories = {
            general: 'General', introduction: 'Introduction', follow_up: 'Follow-up',
            meeting: 'Meeting Request', proposal: 'Proposal', check_in: 'Check-in',
            thank_you: 'Thank You', cold_outreach: 'Cold Outreach',
        };
        return categories[value] || value;
    };
    
    // Load template recommendations for a contact
    const loadRecommendations = async (email) => {
        if (!email) {
            setRecommendations(null);
            return;
        }
        setLoadingRecommendations(true);
        try {
            const res = await axios.get(`${API}/inbox/template-recommendations/${encodeURIComponent(email)}`, {
                headers: getAuthHeader()
            });
            setRecommendations(res.data);
            // Auto-set filter to recommended category
            if (res.data.primary_recommendation) {
                setTemplateFilter(res.data.primary_recommendation);
            }
        } catch (err) {
            console.error('Failed to load recommendations:', err);
            setRecommendations(null);
        } finally {
            setLoadingRecommendations(false);
        }
    };
    
    // Load recommendations when opening template browser
    const openTemplateBrowser = () => {
        if (composeForm.to) {
            loadRecommendations(composeForm.to);
        }
        setShowTemplateBrowser(true);
    };
    
    // Get recommendation score for a category
    const getRecommendationScore = (category) => {
        if (!recommendations?.recommendations) return null;
        const rec = recommendations.recommendations.find(r => r.category === category);
        return rec ? rec.score : null;
    };

    // Log communication
    const handleLogCommunication = async () => {
        if (!logForm.type || !logForm.subject) {
            toast.error('Please fill required fields');
            return;
        }
        try {
            await logCommMutation.mutateAsync(logForm);
            toast.success('Communication logged');
            setShowLogModal(false);
            setLogForm({ type: 'call', contact_id: '', account_id: '', subject: '', notes: '', duration: '' });
        } catch (err) {
            toast.error('Failed to log communication');
        }
    };

    // View email detail
    const viewEmailDetail = (email) => {
        setSelectedEmail(email);
        setShowEmailDetail(true);
    };

    // Format date
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const now = new Date();
        const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return date.toLocaleDateString('en-US', { weekday: 'short' });
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Format meeting date for header
    const formatMeetingDate = (date) => {
        return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    };

    // Navigate meeting date
    const navigateMeetingDate = (direction) => {
        const newDate = new Date(meetingDate);
        newDate.setDate(newDate.getDate() + direction);
        setMeetingDate(newDate);
    };

    // Get status badge color
    const getStatusBadge = (status) => {
        const styles = {
            sent: 'bg-blue-100 text-blue-700',
            opened: 'bg-green-100 text-green-700',
            clicked: 'bg-purple-100 text-purple-700',
            replied: 'bg-amber-100 text-amber-700',
            bounced: 'bg-red-100 text-red-700',
            scheduled: 'bg-stone-100 text-stone-600'
        };
        return styles[status] || 'bg-stone-100 text-stone-600';
    };

    // Strip HTML for preview
    const stripHtml = (html) => {
        if (!html) return '';
        return html.replace(/<[^>]*>/g, '').substring(0, 100);
    };

    const filteredItems = getFilteredItems();
    
    // Check for critical errors
    const hasError = inboxError || (gmailStatus?.connected && gmailEmailsError);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-stone-50">
                <div className="text-center">
                    <div className="w-10 h-10 border-4 border-stone-700 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-stone-500 text-sm">Loading inbox...</p>
                </div>
            </div>
        );
    }
    
    if (hasError) {
        return (
            <div className="flex items-center justify-center h-screen bg-stone-50">
                <div className="text-center max-w-md p-6">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Mail className="w-8 h-8 text-red-600" />
                    </div>
                    <h2 className="text-lg font-semibold text-stone-900 mb-2">Unable to load inbox</h2>
                    <p className="text-sm text-stone-500 mb-4">
                        {inboxError?.message || 'There was a problem loading your emails. Please try refreshing the page.'}
                    </p>
                    <Button onClick={() => window.location.reload()} className="bg-stone-900 hover:bg-stone-800">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh Page
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white flex flex-col" data-testid="inbox-page">
            {/* Top Search Bar */}
            <div className="border-b border-stone-200 px-4 py-3 flex items-center justify-between">
                <div className="relative flex-1 max-w-xl">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                        placeholder="Search..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="pl-9 bg-stone-50 border-stone-200"
                        data-testid="inbox-search"
                    />
                </div>
                <div className="flex items-center gap-2 ml-4">
                    <Button variant="ghost" size="sm" className="text-stone-500">
                        <Phone className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-stone-500">
                        <Settings className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-stone-500">
                        <HelpCircle className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            <div className="flex flex-1 overflow-hidden">
                {/* Main Content */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Inbox Header */}
                    <div className="px-4 py-3 border-b border-stone-100">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <h1 className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    Inbox
                                </h1>
                                <Select value={activeTab} onValueChange={setActiveTab}>
                                    <SelectTrigger className="w-6 h-6 p-0 border-0">
                                        <ChevronLeft className="w-4 h-4 rotate-[-90deg]" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {INBOX_TABS.map(tab => (
                                            <SelectItem key={tab.key} value={tab.key}>{tab.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-center gap-2">
                                {gmailConnected && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleSyncGmail}
                                        disabled={syncGmailMutation.isPending}
                                        data-testid="sync-gmail-btn"
                                    >
                                        {syncGmailMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                                        Sync Gmail
                                    </Button>
                                )}
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowLogModal(true)}
                                    data-testid="log-communication-btn"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Log
                                </Button>
                                <Button
                                    size="sm"
                                    className="bg-stone-900 hover:bg-stone-800"
                                    onClick={() => setShowComposeModal(true)}
                                    data-testid="compose-btn"
                                >
                                    <Send className="w-4 h-4 mr-1" />
                                    Compose
                                </Button>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="flex items-center gap-1 overflow-x-auto pb-1">
                            {INBOX_TABS.map(tab => {
                                const Icon = tab.icon;
                                const count = tabCounts[tab.key] || 0;
                                return (
                                    <button
                                        key={tab.key}
                                        onClick={() => setActiveTab(tab.key)}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                                            activeTab === tab.key
                                                ? 'bg-stone-100 text-stone-900 font-medium'
                                                : 'text-stone-500 hover:bg-stone-100'
                                        }`}
                                        data-testid={`tab-${tab.key}`}
                                    >
                                        <Icon className="w-3.5 h-3.5" />
                                        {tab.label}
                                        {count > 0 && (
                                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                                                activeTab === tab.key ? 'bg-stone-200' : 'bg-stone-200'
                                            }`}>
                                                {count}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                            <div className="ml-auto flex items-center gap-2">
                                {gmailConnected && (
                                    <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full" data-testid="gmail-connected-badge">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                        Gmail
                                    </span>
                                )}
                                {gmailNeedsReconnect && (
                                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/oauth/gmail/login`}
                                        className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full hover:bg-amber-100 transition-colors cursor-pointer">
                                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                        Gmail expired — Reconnect
                                    </a>
                                )}
                                {loadingGmail && (
                                    <Loader2 className="w-3.5 h-3.5 text-stone-400 animate-spin" />
                                )}
                                <span className="text-xs text-stone-400">
                                    Potential Contacts: {potentialContacts.length}
                                </span>
                                <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-xs font-medium text-stone-900">
                                    {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Email Tracking Tab */}
                    {activeTab === 'tracking' ? (
                        <div className="flex-1 overflow-auto p-4">
                            {trackedEmails.length === 0 ? (
                                <div className="text-center py-12 text-stone-400">
                                    <Eye className="w-10 h-10 mx-auto mb-3 opacity-40" />
                                    <p className="font-medium">No tracked emails yet</p>
                                    <p className="text-sm mt-1">Enable the Track toggle when composing to track opens and clicks</p>
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    <div className="grid grid-cols-[1fr_120px_80px_80px_100px] gap-3 px-3 py-2 text-xs font-medium text-stone-400 uppercase tracking-wider border-b border-stone-100">
                                        <span>Recipient / Subject</span>
                                        <span>Status</span>
                                        <span className="text-center">Opens</span>
                                        <span className="text-center">Clicks</span>
                                        <span>Sent</span>
                                    </div>
                                    {trackedEmails.map(email => (
                                        <div
                                            key={email.tracking_id}
                                            className="grid grid-cols-[1fr_120px_80px_80px_100px] gap-3 px-3 py-2.5 rounded-lg hover:bg-stone-50 cursor-pointer transition-colors items-center"
                                            onClick={() => setSelectedEmail({ ...email, _source: 'tracking' })}
                                        >
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-stone-900 truncate">{email.to_email}</p>
                                                <p className="text-xs text-stone-500 truncate">{email.subject}</p>
                                            </div>
                                            <TrackingStatusBadge status={email.status} />
                                            <div className="text-center">
                                                {email.open_count > 0 ? (
                                                    <span className="inline-flex items-center gap-1 text-violet-600">
                                                        <Eye className="w-3.5 h-3.5" />
                                                        <span className="text-sm font-medium">{email.open_count}</span>
                                                    </span>
                                                ) : (
                                                    <span className="text-stone-300">&mdash;</span>
                                                )}
                                            </div>
                                            <div className="text-center">
                                                {email.click_count > 0 ? (
                                                    <span className="inline-flex items-center gap-1 text-blue-600">
                                                        <MousePointer className="w-3.5 h-3.5" />
                                                        <span className="text-sm font-medium">{email.click_count}</span>
                                                    </span>
                                                ) : (
                                                    <span className="text-stone-300">&mdash;</span>
                                                )}
                                            </div>
                                            <span className="text-xs text-stone-400">
                                                {email.sent_at ? new Date(email.sent_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : activeTab === 'whatsapp' ? (
                        <div className="flex-1 overflow-hidden">
                            <WhatsAppChat />
                        </div>
                    ) : activeTab === 'tasks' ? (
                        <InboxTasksTab />
                    ) : (<>

                    {/* Filter Bar */}
                    <div className="px-4 py-2 border-b border-stone-100 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Checkbox
                                checked={selectedIds.length === filteredItems.length && filteredItems.length > 0}
                                onCheckedChange={toggleSelectAll}
                                data-testid="select-all"
                            />
                            <span className="text-sm text-stone-500">Select all</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" className="text-xs">
                                <Filter className="w-3 h-3 mr-1" />
                                Filters
                            </Button>
                            <Button variant="outline" size="sm" className="text-xs">
                                <Calendar className="w-3 h-3 mr-1" />
                                Due date
                            </Button>
                        </div>
                    </div>

                    {/* Email/Communication List */}
                    <div className="flex-1 overflow-y-auto">
                        {filteredItems.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-64 text-stone-400">
                                <Mail className="w-12 h-12 mb-3 opacity-40" />
                                <p className="font-medium text-stone-600">No items in this view</p>
                                <p className="text-sm mt-1 text-stone-400">
                                    {activeTab === 'emails' || activeTab === 'primary' ? (
                                        gmailConnected ? (
                                            'No emails found. Try syncing Gmail or check back later.'
                                        ) : (
                                            'No CRM emails yet. Connect Gmail to see your emails here.'
                                        )
                                    ) : (
                                        `No ${activeTab} items yet. Log some communications to see them here.`
                                    )}
                                </p>
                                {activeTab === 'emails' && gmailConnected && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleSyncGmail}
                                        disabled={syncGmailMutation.isPending}
                                        className="mt-4"
                                    >
                                        {syncGmailMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                                        Sync Gmail Now
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <div className="divide-y divide-stone-100">
                                {filteredItems.map((item) => {
                                    const isEmail = item.email_id;
                                    const isGmail = item._source === 'gmail';
                                    const itemId = item.email_id || item.communication_id;
                                    const isSelected = selectedIds.includes(itemId);
                                    const isPotentialContact = isEmail && !isGmail && !item.contact_id;
                                    
                                    return (
                                        <div
                                            key={itemId}
                                            className={`flex items-center px-4 py-3 hover:bg-stone-50 cursor-pointer transition-colors ${
                                                isSelected ? 'bg-stone-100' : ''
                                            }`}
                                            data-testid={`inbox-item-${itemId}`}
                                        >
                                            {/* Checkbox */}
                                            <div className="mr-3" onClick={e => e.stopPropagation()}>
                                                <Checkbox
                                                    checked={isSelected}
                                                    onCheckedChange={() => toggleSelect(itemId)}
                                                />
                                            </div>

                                            {/* Icon */}
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                                                isGmail ? 'bg-red-100 text-red-600' :
                                                isEmail ? 'bg-blue-100 text-blue-600' : 'bg-stone-100 text-stone-600'
                                            }`}>
                                                {isGmail ? <Mail className="w-4 h-4" /> :
                                                 isEmail ? <Mail className="w-4 h-4" /> : 
                                                 item.type === 'call' ? <Phone className="w-4 h-4" /> :
                                                 item.type === 'message' ? <MessageSquare className="w-4 h-4" /> :
                                                 <CheckSquare className="w-4 h-4" />}
                                            </div>

                                            {/* Content */}
                                            <div 
                                                className="flex-1 min-w-0 mr-4"
                                                onClick={() => isEmail && viewEmailDetail(item)}
                                            >
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    <span className="font-medium text-sm text-stone-800 truncate">
                                                        {isEmail ? item.contact_name || item.contact_email : item.subject}
                                                    </span>
                                                    {isEmail && item.contact_email && (
                                                        <span className="text-xs text-stone-400 truncate hidden sm:inline">
                                                            {item.contact_email}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {isGmail && (
                                                        <span className="inline-flex items-center gap-0.5 text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded font-medium">
                                                            Gmail
                                                        </span>
                                                    )}
                                                    {isPotentialContact && (
                                                        <span className="inline-flex items-center gap-1 text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded">
                                                            <Users className="w-3 h-3" />
                                                            POTENTIAL CONTACT
                                                        </span>
                                                    )}
                                                    {isEmail && item.status && (
                                                        <span className={`text-xs px-2 py-0.5 rounded ${getStatusBadge(item.status)}`}>
                                                            {item.status.toUpperCase()}
                                                        </span>
                                                    )}
                                                    <span className="text-sm text-stone-600 truncate">
                                                        {isEmail ? item.subject : item.notes}
                                                    </span>
                                                    <span className="text-xs text-stone-400 truncate hidden md:inline">
                                                        {stripHtml(isEmail ? item.body : item.notes)}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="flex items-center gap-1 mr-3" onClick={e => e.stopPropagation()}>
                                                {isPotentialContact && (
                                                    <>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 text-xs"
                                                            onClick={() => handleAddContact(item)}
                                                            data-testid={`add-contact-${itemId}`}
                                                        >
                                                            <UserPlus className="w-3 h-3 mr-1" />
                                                            Add
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 text-xs text-stone-400"
                                                            onClick={() => handleDiscard(itemId)}
                                                            data-testid={`discard-${itemId}`}
                                                        >
                                                            <Archive className="w-3 h-3 mr-1" />
                                                            Discard
                                                        </Button>
                                                    </>
                                                )}
                                                {isEmail && !isPotentialContact && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 text-xs"
                                                        onClick={() => handleReply(item)}
                                                    >
                                                        <Reply className="w-3 h-3 mr-1" />
                                                        Reply
                                                    </Button>
                                                )}
                                            </div>

                                            {/* Status indicator */}
                                            {isEmail && (
                                                <div className="flex items-center gap-1 mr-3 text-stone-400">
                                                    {item.opened_at && <Eye className="w-3 h-3 text-green-500" title="Opened" />}
                                                    {item.clicked_at && <MousePointer className="w-3 h-3 text-purple-500" title="Clicked" />}
                                                </div>
                                            )}

                                            {/* Date */}
                                            <div className="text-xs text-red-500 font-medium whitespace-nowrap">
                                                {formatDate(item.sent_at || item.scheduled_date || item.created_at)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </>)}
                </div>

                {/* Meetings Sidebar */}
                {showMeetings && (
                    <div className="w-72 border-l border-stone-200 flex flex-col bg-white">
                        <div className="p-4 border-b border-stone-100 flex items-center justify-between">
                            <h3 className="font-semibold text-stone-800">Meetings</h3>
                            <div className="flex items-center gap-1">
                                {calendarConnected && (
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowScheduleModal(true)} data-testid="schedule-meeting-btn">
                                        <Plus className="w-4 h-4" />
                                    </Button>
                                )}
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowMeetings(false)}>
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                        <div className="p-4 border-b border-stone-100">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-stone-700">
                                    {formatMeetingDate(meetingDate)}
                                </span>
                                <div className="flex items-center gap-1">
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => navigateMeetingDate(-1)}>
                                        <ChevronLeft className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => navigateMeetingDate(1)}>
                                        <ChevronRight className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" className="text-xs ml-1" onClick={() => setMeetingDate(new Date())}>
                                        Today
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4">
                            {!calendarConnected && (
                                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                    <p className="text-xs text-amber-700 mb-2">Connect Google Calendar to see your meetings here.</p>
                                    <a href="/settings?tab=integrations" className="text-xs font-medium text-amber-800 underline" data-testid="connect-calendar-link">Go to Settings</a>
                                </div>
                            )}
                            {meetings.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-stone-400">
                                    <Calendar className="w-10 h-10 mb-3" />
                                    <p className="text-sm text-center">No meetings scheduled</p>
                                    <p className="text-sm font-medium">{formatMeetingDate(meetingDate)}</p>
                                    {calendarConnected && (
                                        <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowScheduleModal(true)} data-testid="schedule-meeting-empty-btn">
                                            <Plus className="w-3 h-3 mr-1" /> Schedule Meeting
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {meetings.map(meeting => {
                                        const startTime = meeting.start ? new Date(meeting.start) : null;
                                        const endTime = meeting.end ? new Date(meeting.end) : null;
                                        const timeStr = startTime && !meeting.all_day
                                            ? `${startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}${endTime ? ' - ' + endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}`
                                            : meeting.all_day ? 'All day' : '';
                                        return (
                                            <div key={meeting.event_id} className="p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors group" data-testid={`meeting-${meeting.event_id}`}>
                                                <p className="font-medium text-sm text-stone-800 truncate">{meeting.title}</p>
                                                {timeStr && <p className="text-xs text-stone-500 mt-0.5">{timeStr}</p>}
                                                {meeting.attendees?.length > 0 && (
                                                    <p className="text-xs text-stone-400 mt-1 truncate">
                                                        {meeting.attendees.map(a => a.name || a.email).join(', ')}
                                                    </p>
                                                )}
                                                {(meeting.hangout_link || meeting.conference_link) && (
                                                    <a href={meeting.conference_link || meeting.hangout_link} target="_blank" rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1 mt-1.5 text-xs text-emerald-600 hover:text-emerald-700">
                                                        <ExternalLink className="w-3 h-3" /> Join Meeting
                                                    </a>
                                                )}
                                                {meeting.html_link && !meeting.hangout_link && !meeting.conference_link && (
                                                    <a href={meeting.html_link} target="_blank" rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1 mt-1.5 text-xs text-stone-500 hover:text-stone-700 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <ExternalLink className="w-3 h-3" /> Open in Calendar
                                                    </a>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Compose Modal */}
            <Dialog open={showComposeModal} onOpenChange={setShowComposeModal}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Compose Email</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {/* To field */}
                        <div>
                            <div className="flex items-center justify-between">
                                <Label>To</Label>
                                <button onClick={() => setShowCcBcc(!showCcBcc)} className="text-[10px] text-stone-400 hover:text-stone-600">
                                    {showCcBcc ? 'Hide CC/BCC' : 'CC / BCC'}
                                </button>
                            </div>
                            <Input
                                value={composeForm.to}
                                onChange={e => setComposeForm(p => ({ ...p, to: e.target.value }))}
                                placeholder="recipient@email.com"
                                className="mt-1"
                                data-testid="compose-to-input"
                            />
                        </div>
                        {showCcBcc && (
                            <>
                                <div>
                                    <label className="block text-[10px] text-stone-500 mb-0.5">CC</label>
                                    <ContactEmailTypeahead value={cc} onChange={setCc} placeholder="Add CC..." />
                                </div>
                                <div>
                                    <label className="block text-[10px] text-stone-500 mb-0.5">BCC</label>
                                    <ContactEmailTypeahead value={bcc} onChange={setBcc} placeholder="Add BCC..." />
                                </div>
                            </>
                        )}

                        {/* Subject with Template button */}
                        <div>
                            <div className="flex items-center justify-between">
                                <Label>Subject</Label>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={openTemplateBrowser}
                                    data-testid="use-template-btn"
                                >
                                    <Layers className="w-3 h-3 mr-1" />
                                    Use Template
                                </Button>
                            </div>
                            <Input
                                value={composeForm.subject}
                                onChange={e => setComposeForm(p => ({ ...p, subject: e.target.value }))}
                                placeholder="Email subject"
                                className="mt-1"
                                data-testid="compose-subject-input"
                            />
                        </div>
                        
                        {/* Body with Signature and Variable selectors */}
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <Label>Body</Label>
                                <div className="flex items-center gap-1">
                                    {/* Signature selector */}
                                    <Select value="" onValueChange={sigId => {
                                        const sig = [...signatures, ...adminSignatures].find(s => s.signature_id === sigId);
                                        if (sig) insertSignature(sig);
                                    }}>
                                        <SelectTrigger className="w-36 h-7 text-xs" data-testid="signature-selector">
                                            <FileSignature className="w-3 h-3 mr-1" />
                                            <SelectValue placeholder="Add Signature" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {signatures.length > 0 && (
                                                <>
                                                    <div className="px-2 py-1 text-xs font-semibold text-stone-400">My Signatures</div>
                                                    {signatures.map(s => (
                                                        <SelectItem key={s.signature_id} value={s.signature_id}>
                                                            {s.is_default && <Star className="w-3 h-3 inline mr-1 text-amber-500" />}
                                                            {s.name}
                                                        </SelectItem>
                                                    ))}
                                                </>
                                            )}
                                            {adminSignatures.length > 0 && (
                                                <>
                                                    <div className="px-2 py-1 text-xs font-semibold text-stone-400 border-t mt-1 pt-1">Templates</div>
                                                    {adminSignatures.map(t => (
                                                        <SelectItem key={t.signature_id} value={t.signature_id}>{t.name}</SelectItem>
                                                    ))}
                                                </>
                                            )}
                                            {signatures.length === 0 && adminSignatures.length === 0 && (
                                                <div className="px-2 py-2 text-xs text-stone-400">No signatures yet</div>
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div data-testid="compose-body-editor">
                                <RichTextEditor
                                    content={composeForm.body}
                                    onChange={html => setComposeForm(p => ({ ...p, body: html }))}
                                    placeholder="Write your email..."
                                    minHeight="200px"
                                    showMergeVariables={true}
                                    showAIWriter={true}
                                    showContentLibrary={true}
                                    onSubjectGenerated={(subj) => setComposeForm(p => ({ ...p, subject: subj }))}
                                />
                            </div>
                            <p className="text-xs text-stone-400 mt-1">
                                Tip: Use variables like {'{{FirstName}}'}, {'{{Company}}'} for personalization
                            </p>
                        </div>
                    </div>
                    <DialogFooter className="flex items-center justify-between sm:justify-between">
                        <div className="flex items-center gap-2 text-xs">
                            {gmailConnected ? (
                                <span className="inline-flex items-center gap-1 text-green-600" data-testid="gmail-status-indicator">
                                    <span className="w-2 h-2 rounded-full bg-green-500" />
                                    Sending via {gmailEmail}
                                </span>
                            ) : gmailNeedsReconnect ? (
                                <span className="inline-flex items-center gap-1 text-amber-600" data-testid="gmail-status-indicator">
                                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                                    Gmail expired — reconnect in Settings
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 text-amber-600" data-testid="gmail-status-indicator">
                                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                                    Gmail not connected
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {gmailConnected && (
                                <label className="flex items-center gap-1.5 cursor-pointer select-none" title="Track opens and link clicks">
                                    <Checkbox
                                        checked={trackEmail}
                                        onCheckedChange={setTrackEmail}
                                        className="w-3.5 h-3.5"
                                    />
                                    <span className="text-xs text-stone-500 flex items-center gap-1">
                                        <Eye className="w-3 h-3" />
                                        Track
                                    </span>
                                </label>
                            )}
                            <Button variant="outline" onClick={() => setShowComposeModal(false)}>Cancel</Button>
                            <Button
                                className="bg-stone-900 hover:bg-stone-800"
                                onClick={handleSendEmail}
                                disabled={sendEmailMutation.isPending}
                                data-testid="send-email-btn"
                            >
                                {sendEmailMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                                {gmailConnected ? 'Send' : 'Log Email'}
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Template Browser Modal */}
            <Dialog open={showTemplateBrowser} onOpenChange={setShowTemplateBrowser}>
                <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Layers className="w-5 h-5 text-stone-700" />
                            Email Templates
                            {recommendations && (
                                <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-normal">
                                    <Sparkles className="w-3 h-3" />
                                    AI Recommended
                                </span>
                            )}
                        </DialogTitle>
                        <DialogDescription>
                            Choose a template as the starting point for your email.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {/* Smart Recommendation Banner */}
                    {loadingRecommendations ? (
                        <div className="flex items-center gap-2 py-3 px-4 bg-stone-100 rounded-lg border border-stone-200">
                            <div className="w-4 h-4 border-2 border-stone-700 border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm text-stone-900">Analyzing contact history...</span>
                        </div>
                    ) : recommendations && (
                        <div className="py-3 px-4 bg-gradient-to-r from-stone-100 to-stone-50 rounded-lg border border-stone-200">
                            <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                                    <Sparkles className="w-4 h-4 text-stone-700" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-stone-800">
                                        {recommendations.recommendation_reason}
                                    </p>
                                    {recommendations.engagement_summary && recommendations.engagement_summary.total_emails > 0 && (
                                        <div className="flex items-center gap-3 mt-2 text-xs text-stone-500">
                                            <span>{recommendations.engagement_summary.total_emails} emails sent</span>
                                            <span>{recommendations.engagement_summary.open_rate}% open rate</span>
                                            {recommendations.engagement_summary.click_rate > 0 && (
                                                <span>{recommendations.engagement_summary.click_rate}% click rate</span>
                                            )}
                                            {recommendations.engagement_summary.replied > 0 && (
                                                <span className="text-green-600">{recommendations.engagement_summary.replied} replies</span>
                                            )}
                                        </div>
                                    )}
                                    {recommendations.recommendations && recommendations.recommendations.length > 0 && (
                                        <div className="flex items-center gap-2 mt-2">
                                            <span className="text-xs text-stone-500">Suggested:</span>
                                            {recommendations.recommendations.slice(0, 3).map(rec => (
                                                <button
                                                    key={rec.category}
                                                    onClick={() => setTemplateFilter(rec.category)}
                                                    className={`text-xs px-2 py-1 rounded-full transition-colors ${
                                                        templateFilter === rec.category
                                                            ? 'bg-stone-900 text-white'
                                                            : 'bg-white border border-stone-200 text-stone-600 hover:border-stone-400'
                                                    }`}
                                                >
                                                    {getCategoryLabel(rec.category)}
                                                    <span className="ml-1 opacity-70">{rec.score}%</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Category Filter */}
                    <div className="flex items-center gap-2 py-2 border-b border-stone-100">
                        <span className="text-sm text-stone-500">Filter:</span>
                        <Select value={templateFilter} onValueChange={setTemplateFilter}>
                            <SelectTrigger className="w-40 h-8">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Categories</SelectItem>
                                <SelectItem value="introduction">Introduction</SelectItem>
                                <SelectItem value="follow_up">Follow-up</SelectItem>
                                <SelectItem value="meeting">Meeting Request</SelectItem>
                                <SelectItem value="proposal">Proposal</SelectItem>
                                <SelectItem value="check_in">Check-in</SelectItem>
                                <SelectItem value="thank_you">Thank You</SelectItem>
                                <SelectItem value="cold_outreach">Cold Outreach</SelectItem>
                                <SelectItem value="general">General</SelectItem>
                            </SelectContent>
                        </Select>
                        <span className="text-xs text-stone-400">
                            {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                    
                    {/* Templates List */}
                    <div className="flex-1 overflow-y-auto py-2 space-y-2">
                        {allTemplates.length === 0 ? (
                            <div className="text-center py-8">
                                <Layers className="w-10 h-10 mx-auto text-stone-300 mb-3" />
                                <p className="text-sm text-stone-500">No email templates available</p>
                                <p className="text-xs text-stone-400 mt-1">Ask your admin to create email templates</p>
                            </div>
                        ) : filteredTemplates.length === 0 ? (
                            <div className="text-center py-8">
                                <p className="text-sm text-stone-500">No templates in this category</p>
                            </div>
                        ) : (
                            filteredTemplates.map(template => {
                                const recScore = getRecommendationScore(template.category);
                                const isRecommended = recScore && recScore >= 80;
                                return (
                                <div
                                    key={template.template_id}
                                    className={`border rounded-lg p-3 hover:border-stone-400 hover:bg-stone-100/30 transition-colors cursor-pointer group ${
                                        isRecommended ? 'border-green-200 bg-green-50/30' : 'border-stone-200'
                                    }`}
                                    onClick={() => applyTemplate(template)}
                                    data-testid={`template-${template.template_id}`}
                                >
                                    <div className="flex items-start justify-between mb-1">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-medium text-sm text-stone-800">{template.name}</span>
                                                <span className="text-xs bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded">
                                                    {getCategoryLabel(template.category)}
                                                </span>
                                                {template.is_admin_template && (
                                                    <span className="text-xs bg-stone-100 text-stone-700 px-1.5 py-0.5 rounded">
                                                        Admin
                                                    </span>
                                                )}
                                                {isRecommended && (
                                                    <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                                        <Sparkles className="w-3 h-3" />
                                                        {recScore}% match
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-stone-600 mt-1 font-medium">
                                                Subject: {template.subject}
                                            </p>
                                        </div>
                                        <Button
                                            size="sm"
                                            className={`h-7 opacity-0 group-hover:opacity-100 transition-opacity ${
                                                isRecommended ? 'bg-green-600 hover:bg-green-700' : 'bg-stone-900 hover:bg-stone-800'
                                            }`}
                                        >
                                            Use
                                        </Button>
                                    </div>
                                    <div
                                        className="text-xs text-stone-500 line-clamp-2 mt-2 prose prose-sm max-w-none"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template.body) }}
                                    />
                                </div>
                                );
                            })
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Log Communication Modal */}
            <Dialog open={showLogModal} onOpenChange={setShowLogModal}>
                <DialogContent className={`transition-all duration-300 ${logModalExpanded ? 'sm:max-w-3xl' : 'sm:max-w-md'}`}>
                    <DialogHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <DialogTitle>Log Communication</DialogTitle>
                                <DialogDescription>Record a call, message, or task</DialogDescription>
                            </div>
                            <button
                                onClick={() => setLogModalExpanded(prev => !prev)}
                                className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors"
                                data-testid="expand-log-modal-btn"
                                title={logModalExpanded ? 'Collapse' : 'Expand'}
                            >
                                {logModalExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                            </button>
                        </div>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className={logModalExpanded ? 'grid grid-cols-2 gap-4' : ''}>
                            <div>
                                <Label>Type</Label>
                                <Select value={logForm.type} onValueChange={v => setLogForm(p => ({ ...p, type: v }))}>
                                    <SelectTrigger className="mt-1">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="call">Call</SelectItem>
                                        <SelectItem value="message">Message</SelectItem>
                                        <SelectItem value="task">Task</SelectItem>
                                        <SelectItem value="reminder">Reminder</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Contact (Optional)</Label>
                                <Select value={logForm.contact_id || "none"} onValueChange={v => setLogForm(p => ({ ...p, contact_id: v === "none" ? "" : v }))}>
                                    <SelectTrigger className="mt-1">
                                        <SelectValue placeholder="Select contact" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">No contact</SelectItem>
                                        {contacts.map(c => (
                                            <SelectItem key={c.contact_id} value={c.contact_id}>
                                                {c.first_name} {c.last_name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label>Subject</Label>
                            <div className="relative mt-1">
                                <Input
                                    value={logForm.subject}
                                    onChange={e => setLogForm(p => ({ ...p, subject: e.target.value }))}
                                    placeholder="Brief description"
                                    className="pr-10"
                                    data-testid="log-subject-input"
                                />
                                <VoiceButton
                                    onResult={(text) => setLogForm(p => ({ ...p, subject: (p.subject ? p.subject + ' ' : '') + text }))}
                                    testId="voice-subject-btn"
                                />
                            </div>
                        </div>
                        <div>
                            <Label>Notes</Label>
                            <div className="relative mt-1">
                                <Textarea
                                    value={logForm.notes}
                                    onChange={e => setLogForm(p => ({ ...p, notes: e.target.value }))}
                                    placeholder="Additional details..."
                                    rows={logModalExpanded ? 8 : 3}
                                    className="pr-10"
                                    data-testid="log-notes-input"
                                />
                                <VoiceButton
                                    onResult={(text) => setLogForm(p => ({ ...p, notes: (p.notes ? p.notes + ' ' : '') + text }))}
                                    testId="voice-notes-btn"
                                    isTextarea
                                />
                            </div>
                        </div>
                        {logForm.type === 'call' && (
                            <div>
                                <Label>Duration (minutes)</Label>
                                <Input
                                    type="number"
                                    value={logForm.duration}
                                    onChange={e => setLogForm(p => ({ ...p, duration: e.target.value }))}
                                    placeholder="15"
                                    className="mt-1"
                                />
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowLogModal(false)}>Cancel</Button>
                        <Button onClick={handleLogCommunication} className="bg-stone-900 hover:bg-stone-800">
                            Log Communication
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Email Detail Modal */}
            <Dialog open={showEmailDetail} onOpenChange={setShowEmailDetail}>
                <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden p-0">
                    {selectedEmail && (
                        <div className="flex flex-col h-full max-h-[85vh]">
                            {/* Email Header */}
                            <div className="px-6 py-4 border-b border-stone-200 bg-white">
                                <h2 className="text-lg font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {selectedEmail.subject || '(No subject)'}
                                </h2>
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center text-sm font-bold text-stone-600 flex-shrink-0">
                                        {(selectedEmail.from || selectedEmail.contact_name || '?').charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-stone-900">
                                                {selectedEmail._source === 'gmail'
                                                    ? selectedEmail.from || selectedEmail.contact_name
                                                    : selectedEmail.contact_name || selectedEmail.contact_email}
                                            </span>
                                            {selectedEmail._source === 'gmail' && (
                                                <span className="inline-flex items-center gap-1 text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded font-medium">Gmail</span>
                                            )}
                                        </div>
                                        <div className="text-xs text-stone-400">
                                            To: {selectedEmail.to || selectedEmail.contact_email}
                                            <span className="mx-2">|</span>
                                            {(() => {
                                                const d = new Date(selectedEmail.sent_at || selectedEmail.date || selectedEmail.scheduled_date);
                                                return isNaN(d.getTime()) ? '' : d.toLocaleString();
                                            })()}
                                        </div>
                                    </div>
                                </div>
                                {/* Tracking badges */}
                                {!selectedEmail._source && (selectedEmail.opened_at || selectedEmail.clicked_at) && (
                                    <div className="flex items-center gap-2 mt-3">
                                        {selectedEmail.opened_at && (
                                            <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full">
                                                <Eye className="w-3 h-3" />
                                                Opened {new Date(selectedEmail.opened_at).toLocaleString()}
                                            </span>
                                        )}
                                        {selectedEmail.clicked_at && (
                                            <span className="inline-flex items-center gap-1 text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded-full">
                                                <MousePointer className="w-3 h-3" />
                                                Clicked {new Date(selectedEmail.clicked_at).toLocaleString()}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Email Body */}
                            <div className="flex-1 overflow-y-auto bg-white">
                                <div className="px-6 py-6">
                                    <div
                                        className="prose prose-stone prose-sm max-w-none
                                            prose-p:text-stone-700 prose-p:leading-relaxed prose-p:my-2
                                            prose-a:text-blue-600 prose-a:underline
                                            prose-img:rounded-lg prose-img:max-w-full
                                            prose-headings:text-stone-900"
                                        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', fontSize: '14px', lineHeight: '1.6' }}
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedEmail.body || selectedEmail.snippet || '<p style="color:#a8a29e">No content</p>') }}
                                    />
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="px-6 py-3 border-t border-stone-200 bg-stone-50 flex items-center justify-end gap-2">
                                <Button variant="outline" size="sm" onClick={() => setShowEmailDetail(false)}>Close</Button>
                                <Button size="sm" onClick={() => {
                                    setShowEmailDetail(false);
                                    const replyTo = selectedEmail._source === 'gmail'
                                        ? selectedEmail.contact_email || selectedEmail.from
                                        : selectedEmail.contact_email;
                                    setComposeForm({
                                        to: replyTo,
                                        subject: `Re: ${selectedEmail.subject}`,
                                        body: defaultSignature ? `<p><br></p>${defaultSignature.content}` : '',
                                        type: 'email',
                                    });
                                    setShowComposeModal(true);
                                }}>
                                    <Reply className="w-4 h-4 mr-1" />
                                    Reply
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Schedule Meeting Modal */}
            <ScheduleMeetingModal
                open={showScheduleModal}
                onClose={() => setShowScheduleModal(false)}
                getAuthHeader={getAuthHeader}
                onCreated={(ev) => {
                    queryClient.invalidateQueries({ queryKey: ['calendar', 'events'] });
                }}
            />
        </div>
    );
}
